import { useState, useEffect } from 'react';
import { getNamusImageProxyUrl, parseNamusImageUrl } from '@/lib/api';
import { Loader2, User, ImageOff } from 'lucide-react';

/**
 * NamusImage Component
 * Handles images with automatic proxy fallback for NamUs CORS/404 issues.
 * For non-NamUs URLs (like FBI, Charley Project), loads directly in browser.
 * Automatically converts HTTP to HTTPS for security.
 */
export default function NamusImage({ 
  src, 
  alt, 
  className = '', 
  onError, 
  fallbackSrc = null,
  showLoader = true,
  placeholderType = 'person', // 'person' or 'generic'
  ...props 
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    // Reset state when src changes
    setLoading(true);
    setError(false);
    
    // Ensure HTTPS for security (mixed content blocking)
    const secureSrc = src.replace(/^http:\/\//i, 'https://');
    
    // Check for known placeholder images (treat as no image)
    if (secureSrc.includes('No_Image_Available') || 
        secureSrc.includes('no_image') ||
        secureSrc.includes('placeholder')) {
      setLoading(false);
      setError(true);
      return;
    }
    
    // Check if this is a NamUs URL
    const namusIds = parseNamusImageUrl(secureSrc);
    
    if (namusIds) {
      // For NamUs URLs, use our proxy immediately since direct access usually fails (CORS)
      const proxyUrl = getNamusImageProxyUrl(namusIds.namusCaseId, namusIds.imageId, namusIds.caseType);
      setImageSrc(proxyUrl);
      setLoading(false);
    } else {
      // For non-NamUs URLs (FBI, Charley Project, etc.), load directly in browser
      setImageSrc(secureSrc);
      setLoading(false);
    }
  }, [src]);

  const handleImageError = () => {
    setError(true);
    if (fallbackSrc) {
      setImageSrc(fallbackSrc);
      setError(false);
    }
    if (onError) onError();
  };

  if (loading && showLoader) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 ${className}`} {...props}>
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !fallbackSrc) {
    // Show a nice placeholder icon instead of text
    const PlaceholderIcon = placeholderType === 'person' ? User : ImageOff;
    return (
      <div 
        className={`flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 text-gray-400 dark:text-gray-500 ${className}`} 
        {...props}
      >
        <PlaceholderIcon className="w-1/2 h-1/2 max-w-8 max-h-8 opacity-50" />
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      onError={handleImageError}
      {...props}
    />
  );
}
